"use client";

import { FC, useContext, useEffect, useState } from "react";
import MaxWidthWrapper from "./MaxWidthWrapper";
import { FileMaker } from "./FileMaker";
import JWTContext from "./JWTProvider";
import UserContext from "./UserProvider";
import { useRouter } from "next/navigation";

interface RenewProps {}

const Renew: FC<RenewProps> = ({}) => {
  const [lastName, setLastName] = useState("");
  const [CMSNo, setCMSNo] = useState("");
  const [COMLNo, setCOMLNo] = useState("");
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [loading, setLoading] = useState(false);
  const [invoiceItems] = useState<any[]>([]);
  const { JWT } = useContext(JWTContext);
  const { user, setUser } = useContext(UserContext);
  const router = useRouter();
  const [isAutoFilled, setIsAutoFilled] = useState(false);

  // The WordPress /renew page embeds this app in a same-origin iframe whose
  // src carries no query string, so the emailed ?t= token only exists on the
  // parent page's URL. Fall back to the parent frame's query when ours is empty.
  const getTokenParam = () => {
    let t = new URLSearchParams(window.location.search).get("t");
    if (!t && window.top !== window.self) {
      try {
        t = new URLSearchParams(window.top!.location.search).get("t");
      } catch {
        // cross-origin parent - cannot read, keep t = null
      }
    }
    return t;
  };

  useEffect(() => {
    const hexParam = getTokenParam();
    if (hexParam) {
      const decimalValue = parseInt(hexParam, 16);
      const cmsId = Math.floor(decimalValue / 3500641);
      // Limit automatic attempts per token: if the notice page keeps bouncing
      // us back here for this same token, show the lookup form instead of
      // redirecting again - prevents an infinite redirect loop. Two attempts
      // are allowed (not one) because FileMaker fails transiently under load
      // and a real notice can appear on retry.
      const attemptKey = `tokenAttempts:${hexParam}`;
      const attempts = Number(sessionStorage.getItem(attemptKey) || "0");
      if (attempts >= 2) {
        setCMSNo(cmsId.toString());
        setErrors({
          form: "We couldn't load your membership notice. Please try again in a few minutes using the button below, or contact the CMS membership department at (720) 859-1001.",
        });
        setIsAutoFilled(false);
        sessionStorage.removeItem(attemptKey);
        return;
      }
      sessionStorage.setItem(attemptKey, String(attempts + 1));
      sessionStorage.setItem('cmsID', cmsId.toString());
      setCMSNo(cmsId.toString());
      setIsAutoFilled(true);
      router.push(`/renew/${cmsId}/membership-notice`);
    } else {
      sessionStorage.removeItem('cmsID');
      setCMSNo("");
      setIsAutoFilled(false);
    }
  }, []);

  const validateForm = () => {
    const formErrors: { [key: string]: string } = {};
    const hexParam = getTokenParam();
    if (!hexParam) {
      if (!lastName) formErrors.lastName = "Last name is required.";
    }
    if (!CMSNo && !COMLNo) {
      formErrors.identifier =
        "Either CMS # or CO medical license # is required.";
    }

    setErrors(formErrors);
    return Object.keys(formErrors).length === 0;
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    sessionStorage.setItem("invoiceItems", JSON.stringify(invoiceItems));

    if (!validateForm()) return;
    setLoading(true);
    try {
      let userSearch = await new FileMaker(JWT).findMemberForRenewal(
        lastName,
        CMSNo,
        COMLNo
      );

      if (!userSearch.recordId) {
        setErrors({ form: "User not found with the provided details." });
        setLoading(false);
        return;
      }

      setUser(userSearch);
      router.push(`/renew/${userSearch.fieldData["CMS ID"]}/membership-notice`);
    } catch (error) {
      console.error("Error finding user:", error);
      setErrors({ form: "An error occurred while searching for the user." });
      setLoading(false);
    }
  };

  return (
    <MaxWidthWrapper>
      <h1 className="text-5xl font-bold mb-2">Renew</h1>
      <h2 className="text-3xl mb-5 text-gray-400">
        Your medical society membership
      </h2>
      <hr />
      <p className="font-bold mb-1 mt-5">
        Thank you for renewing your membership in the Colorado Medical Society.
      </p>
      <p>
        Each member increases our influence and amplifies our voice in the
        legislature, the courtroom and the boardroom. And when you renew, you
        stay connected to your colleagues around the state and to our many
        member benefits.
      </p>
      <div className="flex flex-col border border-gray-300 p-4 rounded mb-4 bg-gray-100 mt-5">
        <h3 className="font-bold text-2xl my-3">Find my account</h3>
        {errors.form && (
          <p className="text-red-500 font-semibold mb-2">{errors.form}</p>
        )}
        {!isAutoFilled && (
          <p>
            For us to locate your account, please provide your{" "}
            <strong>last name</strong> and either A) your{" "}
            <strong>CMS #</strong>, which can be found in the upper right
            corner of your membership notice; or B) your{" "}
            <strong>CO medical license #</strong>:
          </p>
        )}
        <form onSubmit={handleSubmit}>
          <div className="mt-10 flex flex-col gap-5">
            {!isAutoFilled && (
              <>
                <div className="sm:col-span-4">
                  <label
                    htmlFor="lastname"
                    className="block text-sm font-medium leading-6 text-gray-900"
                  >
                    Last name
                  </label>
                  <div className="mt-2">
                    <div className="flex rounded-md shadow-sm ring-1 ring-inset ring-gray-300 focus-within:ring-2 focus-within:ring-inset focus-within:ring-indigo-600 sm:max-w-md">
                      <input
                        type="text"
                        name="lastname"
                        id="lastname"
                        autoComplete="lastname"
                        value={lastName}
                        onChange={(e) => setLastName(e.target.value)}
                        className="block flex-1 border-0 bg-transparent py-1.5 pl-1 text-gray-900 placeholder:text-gray-400 focus:ring-0 sm:text-sm sm:leading-6 bg-white rounded-md border-gray-400"
                        placeholder="Last name"
                      />
                    </div>
                    {errors.lastName && (
                      <p className="text-red-500 text-sm mt-1">
                        {errors.lastName}
                      </p>
                    )}
                  </div>
                </div>
                <hr />
              </>
            )}
            <p>
              {isAutoFilled
                ? "We found your account based on the provided information."
                : "Please provide your CMS # (which can be found in the upper right corner of your renewal notice); or your CO medical license #:"}
            </p>
            <div className="flex flex-row gap-5">
              <div className="flex-1">
                <label
                  htmlFor="CMSNo"
                  className="block text-sm font-medium leading-6 text-gray-900"
                >
                  A) CMS #
                </label>
                <div className="mt-2">
                  <div className="flex flex-col">
                    <input
                      type="text"
                      name="CMSNo"
                      id="CMSNo"
                      autoComplete="CMSNo"
                      value={CMSNo}
                      onChange={(e) => setCMSNo(e.target.value)}
                      className="block flex-1 border-0 bg-transparent py-1.5 pl-1 text-gray-900 placeholder:text-gray-400 focus:ring-0 sm:text-sm sm:leading-6 bg-white rounded-md border-gray-400"
                      placeholder="####"
                      readOnly={isAutoFilled}
                    />
                    <p className="text-xs text-gray-400 mt-1">
                      {isAutoFilled
                        ? "CMS # auto-filled based on the provided link."
                        : "Available in the upper right of your renewal notice."}
                    </p>
                  </div>
                </div>
              </div>
              {!isAutoFilled && (
                <>
                  <h3 className="font-bold flex items-center justify-center flex-1">
                    OR
                  </h3>
                  <div className="flex-1">
                    <label
                      htmlFor="COMLNo"
                      className="block text-sm font-medium leading-6 text-gray-900"
                    >
                      B) CO Medical License #
                    </label>
                    <div className="mt-2">
                      <div className="flex flex-col">
                        <input
                          type="text"
                          name="COMLNo"
                          id="COMLNo"
                          autoComplete="COMLNo"
                          value={COMLNo}
                          onChange={(e) => setCOMLNo(e.target.value)}
                          className="block flex-1 border-0 bg-transparent py-1.5 pl-1 text-gray-900 placeholder:text-gray-400 focus:ring-0 sm:text-sm sm:leading-6 bg-white rounded-md border-gray-400"
                          placeholder="#####"
                        />
                        <p className="text-xs text-gray-400 mt-1">
                          You do not need to include the &#34;DR&#34; or
                          leading zeroes (&#34;0&#34;).
                        </p>
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>
            {errors.identifier && (
              <div className="flex justify-center mt-1">
                <p className="text-red-500 text-sm mt-1">
                  {errors.identifier}
                </p>
              </div>
            )}
            <button
              type="submit"
              disabled={loading}
              className="mt-5 rounded-md p-5 border-gray-300 border bg-orange-300 hover:bg-orange-400 font-bold text-lg"
            >
              {loading ? "Loading..." : "Renew Now"}
            </button>
          </div>
        </form>
      </div>
    </MaxWidthWrapper>
  );
};

export default Renew;
